import {
  Box,
  Column,
  Heading,
  Indent,
  parseIntElseUndefined,
  Row,
  Text,
} from "@stenajs-webui/core";
import {
  createColumnConfig,
  StandardTableCellRenderer,
  StandardTableConfigWithGroups,
} from "@stenajs-webui/grid";
import { EditableEntityState } from "@stenajs-webui/redux";
import _, { sumBy } from "lodash";
import { FC } from "react";
import { Link } from "@/components/Link";
import { limitStringLength } from "@/lib/StringLimiteter";
import { routeFactory } from "@/RouteFactory";
import { ExpectedVolumeTableRow } from "../types";
import {
  accompaniedColumnOrder,
  noLabelColumnOrder,
  unaccompaniedColumnOrder,
} from "./ExpectedVolumeColumnOrder";
import { expectedVolumeActions } from "../redux/ExpectedVolumeRedux";
import { Dispatch } from "redux";
import { createEditableCellRenderer } from "@/components/EditableTableCell";

export type ExpectedVolumeTableRowOmitted =
  | keyof Omit<
      ExpectedVolumeTableRow,
      | "id"
      | "volumeAgreed"
      | "customerIsParent"
      | "customerId"
      | "customerNo"
      | "customerIndex"
    >;

export type ExpectedVolumeColumns =
  | ExpectedVolumeTableRowOmitted
  | "totalVolume"
  | "expectedVolume";

const RoutePairTableCustomerLink: FC<{
  label: string;
  customer: EditableEntityState<ExpectedVolumeTableRow>;
}> = ({ label, customer }) => {
  return (
    <Link
      title={label}
      style={{ textDecoration: "none" }}
      to={routeFactory.productAndPrice.customer.customer({
        customerId: customer.id ?? "",
      })}
    >
      {limitStringLength(label, 40)}
    </Link>
  );
};

export const createExpectedVolumeTableConfig = (
  routePairId: string,
  yearIsEditable: boolean,
  dispatch: Dispatch
): StandardTableConfigWithGroups<
  EditableEntityState<ExpectedVolumeTableRow>,
  ExpectedVolumeColumns
> => {
  const onFieldUpdate = (
    field: keyof ExpectedVolumeTableRow,
    item: EditableEntityState<ExpectedVolumeTableRow>,
    value: string | undefined
  ) =>
    dispatch(
      expectedVolumeActions.setFieldsForRow(routePairId, item.persisted.id, {
        [field]: parseIntElseUndefined(value ?? ""),
      })
    );

  return {
    keyResolver: (item) => item.editable.id,
    disableSorting: true,
    disableInfiniteList: true,
    enableGridCell: true,
    columns: {
      customerName: createColumnConfig((item) => "", {
        columnLabel: "Customer",
        disableGridCell: true,
        width: "270px",
        renderCell: ({ label, value, item }) => (
          <Indent overflow={"hidden"}>
            <Text whiteSpace={"nowrap"}>
              <RoutePairTableCustomerLink
                label={`${item.editable.customerIndex} (${item.editable.customerNo}) ${label}`}
                customer={item}
              />
            </Text>
          </Indent>
        ),
      }),
      totalVolume: createColumnConfig((item) => item.editable, {
        columnLabel: "Total volume",
        disableGridCell: true,
        renderCell: ({ item }) => (
          <Indent overflow={"hidden"}>
            <Text whiteSpace={"nowrap"}>
              {_.sum([
                item.editable.accompanied12MonthVolOffPeak,
                item.editable.accompanied12MonthVolPeak,
                item.editable.accompanied12MonthVolShoulder,
                item.editable.unaccompanied12MonthVolOffPeak,
                item.editable.unaccompanied12MonthVolPeak,
                item.editable.unaccompanied12MonthVolShoulder,
              ])}
            </Text>
          </Indent>
        ),
        summaryText: ({ items }) =>
          String(
            sumBy(items, (item) =>
              _.sum([
                item.editable.accompanied12MonthVolOffPeak,
                item.editable.accompanied12MonthVolPeak,
                item.editable.accompanied12MonthVolShoulder,
                item.editable.unaccompanied12MonthVolOffPeak,
                item.editable.unaccompanied12MonthVolPeak,
                item.editable.unaccompanied12MonthVolShoulder,
              ])
            )
          ),
      }),
      expectedVolume: createColumnConfig((item) => item.editable, {
        columnLabel: "Expected volume",
        disableGridCell: true,
        renderCell: ({ item }) => (
          <Indent overflow={"hidden"}>
            <Text whiteSpace={"nowrap"}>
              {_.sum([
                item.editable.accompaniedExpectedVolOffPeak,
                item.editable.accompaniedExpectedVolPeak,
                item.editable.accompaniedExpectedVolShoulder,
                item.editable.unaccompaniedExpectedVolOffPeak,
                item.editable.unaccompaniedExpectedVolPeak,
                item.editable.unaccompaniedExpectedVolShoulder,
              ])}
            </Text>
          </Indent>
        ),
        summaryText: ({ items }) =>
          String(
            sumBy(
              items,
              (item) =>
                _.sum([
                  item.editable.accompaniedExpectedVolOffPeak,
                  item.editable.accompaniedExpectedVolPeak,
                  item.editable.accompaniedExpectedVolShoulder,
                  item.editable.unaccompaniedExpectedVolOffPeak,
                  item.editable.unaccompaniedExpectedVolPeak,
                  item.editable.unaccompaniedExpectedVolShoulder,
                ]) ?? 0
            )
          ),
      }),
      accompanied12MonthVolPeak: createColumnConfig(
        (item) => item.editable.accompanied12MonthVolPeak,
        {
          columnLabel: "Last 12m",
          borderLeft: true,
          summaryText: ({ items }) =>
            String(
              sumBy(
                items,
                (item) => item.editable.accompanied12MonthVolPeak ?? 0
              )
            ),
        }
      ),
      accompanied12MonthVolShoulder: createColumnConfig(
        (item) => item.editable.accompanied12MonthVolShoulder,
        {
          columnLabel: "Last 12m",
          borderLeft: true,
          summaryText: ({ items }) =>
            String(
              sumBy(
                items,
                (item) => item.editable.accompanied12MonthVolShoulder ?? 0
              )
            ),
        }
      ),
      accompanied12MonthVolOffPeak: createColumnConfig(
        (item) => item.editable.accompanied12MonthVolOffPeak,
        {
          columnLabel: "Last 12m",
          borderLeft: true,
          summaryText: ({ items }) =>
            String(
              sumBy(
                items,
                (item) => item.editable.accompanied12MonthVolOffPeak ?? 0
              )
            ),
        }
      ),
      accompaniedExpectedVolPeak: createColumnConfig(
        (item) => String(item.editable.accompaniedExpectedVolPeak ?? ""),
        {
          columnLabel: "Expected",
          isEditable: true,
          renderCell: !yearIsEditable
            ? undefined
            : createEditableCellRenderer<ExpectedVolumeTableRow>(
                "accompaniedExpectedVolPeak"
              ),
          onChange: (item, value) =>
            onFieldUpdate("accompaniedExpectedVolPeak", item, value),
          disableGridCell: !yearIsEditable,
          summaryText: ({ items }) =>
            String(
              sumBy(
                items,
                (item) => item.editable.accompaniedExpectedVolPeak ?? 0
              )
            ),
        }
      ),
      accompaniedExpectedVolShoulder: createColumnConfig(
        (item) => String(item.editable.accompaniedExpectedVolShoulder ?? ""),
        {
          columnLabel: "Expected",
          isEditable: true,
          renderCell: !yearIsEditable
            ? undefined
            : createEditableCellRenderer<ExpectedVolumeTableRow>(
                "accompaniedExpectedVolShoulder"
              ),
          onChange: (item, value) =>
            onFieldUpdate("accompaniedExpectedVolShoulder", item, value),
          disableGridCell: !yearIsEditable,
          summaryText: ({ items }) =>
            String(
              sumBy(
                items,
                (item) => item.editable.accompaniedExpectedVolShoulder ?? 0
              )
            ),
        }
      ),
      accompaniedExpectedVolOffPeak: createColumnConfig(
        (item) => String(item.editable.accompaniedExpectedVolOffPeak ?? ""),
        {
          columnLabel: "Expected",
          isEditable: true,
          renderCell: !yearIsEditable
            ? undefined
            : createEditableCellRenderer<ExpectedVolumeTableRow>(
                "accompaniedExpectedVolOffPeak"
              ),
          onChange: (item, value) =>
            onFieldUpdate("accompaniedExpectedVolOffPeak", item, value),
          disableGridCell: !yearIsEditable,
          summaryText: ({ items }) =>
            String(
              sumBy(
                items,
                (item) => item.editable.accompaniedExpectedVolOffPeak ?? 0
              )
            ),
        }
      ),
      unaccompanied12MonthVolPeak: createColumnConfig(
        (item) => item.editable.unaccompanied12MonthVolPeak,
        {
          columnLabel: "Last 12m",
          borderLeft: true,
          summaryText: ({ items }) =>
            String(
              sumBy(
                items,
                (item) => item.editable.unaccompanied12MonthVolPeak ?? 0
              )
            ),
        }
      ),
      unaccompanied12MonthVolShoulder: createColumnConfig(
        (item) => item.editable.unaccompanied12MonthVolShoulder,
        {
          columnLabel: "Last 12m",
          borderLeft: true,
          summaryText: ({ items }) =>
            String(
              sumBy(
                items,
                (item) => item.editable.unaccompanied12MonthVolShoulder ?? 0
              )
            ),
        }
      ),
      unaccompanied12MonthVolOffPeak: createColumnConfig(
        (item) => item.editable.unaccompanied12MonthVolOffPeak,
        {
          columnLabel: "Last 12m",
          borderLeft: true,
          summaryText: ({ items }) =>
            String(
              sumBy(
                items,
                (item) => item.editable.unaccompanied12MonthVolOffPeak ?? 0
              )
            ),
        }
      ),
      unaccompaniedExpectedVolPeak: createColumnConfig(
        (item) => String(item.editable.unaccompaniedExpectedVolPeak ?? ""),
        {
          columnLabel: "Expected",
          isEditable: true,
          renderCell: !yearIsEditable
            ? undefined
            : createEditableCellRenderer<ExpectedVolumeTableRow>(
                "unaccompaniedExpectedVolPeak"
              ),
          onChange: (item, value) =>
            onFieldUpdate("unaccompaniedExpectedVolPeak", item, value),
          disableGridCell: !yearIsEditable,
          summaryText: ({ items }) =>
            String(
              sumBy(
                items,
                (item) => item.editable.unaccompaniedExpectedVolPeak ?? 0
              )
            ),
        }
      ),
      unaccompaniedExpectedVolShoulder: createColumnConfig(
        (item) => String(item.editable.unaccompaniedExpectedVolShoulder ?? ""),
        {
          columnLabel: "Expected",
          isEditable: true,
          renderCell: !yearIsEditable
            ? undefined
            : createEditableCellRenderer<ExpectedVolumeTableRow>(
                "unaccompaniedExpectedVolShoulder"
              ),
          onChange: (item, value) =>
            onFieldUpdate("unaccompaniedExpectedVolShoulder", item, value),
          disableGridCell: !yearIsEditable,
          summaryText: ({ items }) =>
            String(
              sumBy(
                items,
                (item) => item.editable.unaccompaniedExpectedVolShoulder ?? 0
              )
            ),
        }
      ),
      unaccompaniedExpectedVolOffPeak: createColumnConfig(
        (item) => String(item.editable.unaccompaniedExpectedVolOffPeak ?? ""),
        {
          columnLabel: "Expected",
          isEditable: true,
          renderCell: !yearIsEditable
            ? undefined
            : createEditableCellRenderer<ExpectedVolumeTableRow>(
                "unaccompaniedExpectedVolOffPeak"
              ),
          onChange: (item, value) =>
            onFieldUpdate("unaccompaniedExpectedVolOffPeak", item, value),
          disableGridCell: !yearIsEditable,
          summaryText: ({ items }) =>
            String(
              sumBy(
                items,
                (item) => item.editable.unaccompaniedExpectedVolOffPeak ?? 0
              )
            ),
        }
      ),
    },
    columnGroups: {
      noLabelColumnGroup: {
        columnOrder: noLabelColumnOrder,
      },
      accompanied: {
        columnOrder: accompaniedColumnOrder,
        label: "Accompanied",
        borderLeft: true,
        render: () => <SailingCategoryHeaders heading="Accompanied" />,
      },

      unaccompanied: {
        columnOrder: unaccompaniedColumnOrder,
        label: "Unaccompanied",
        borderLeft: true,
        render: () => <SailingCategoryHeaders heading="Unaccompanied" />,
      },
    },
    columnGroupOrder: ["noLabelColumnGroup", "accompanied", "unaccompanied"],
  };
};

const SailingCategoryHeaders = ({ heading }: { heading: string }) => (
  <Box width={"100%"} gap={1}>
    <Row indent={1} justifyContent={"center"}>
      <Heading variant="h4">{heading}</Heading>
    </Row>
    <Row>
      <Column width={"33%"}>
        <Heading variant="h5">Peak</Heading>
      </Column>
      <Column width={"33%"}>
        <Heading variant="h5">Shoulder</Heading>
      </Column>
      <Column width={"33%"}>
        <Heading variant="h5">Off-peak</Heading>
      </Column>
    </Row>
  </Box>
);
